import * as React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, Rocket } from 'lucide-react';

interface FormNavigationProps {
  onNext: () => void;
  onPrevious: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  isSubmitting?: boolean;
  showConfirmation?: boolean;
  onCancelConfirmation?: () => void;
}

/**
 * Form navigation component with next/previous buttons
 */
export const FormNavigation: React.FC<FormNavigationProps> = ({ 
  onNext, 
  onPrevious, 
  isFirstStep, 
  isLastStep,
  isSubmitting = false,
  showConfirmation = false,
  onCancelConfirmation = () => {}
}) => {
  return (
    <div className="flex justify-between mt-8">
      {!isFirstStep ? (
        <Button
          type="button"
          variant="ghost"
          onClick={onPrevious}
          className="text-white/80 hover:text-white hover:bg-white/5"
        >
          <ArrowLeft className="mr-2 w-4 h-4" />
          Previous
        </Button>
      ) : (
        <>{/* Empty div for spacing when on first step */}</>
      )}

      {/* Show confirmation buttons when on last step and confirmation is showing */}
      {isLastStep && showConfirmation ? (
        <div className="flex gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancelConfirmation}
            disabled={isSubmitting}
            className="bg-gray-800 border border-gray-600 text-white hover:bg-gray-700"
          >
            Go Back
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="bg-gradient-to-r from-rose-500 to-fuchsia-500 hover:from-rose-600 hover:to-fuchsia-600 text-white"
          >
            {isSubmitting ? (
              "Processing..."
            ) : (
              <>
                Confirm & Submit
                <Rocket className="ml-2 w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          onClick={onNext}
          disabled={isSubmitting}
          className="bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90 text-white"
        >
          {isSubmitting ? (
            "Processing..."
          ) : isLastStep ? (
            <>
              Review & Submit
              <Rocket className="ml-2 w-4 h-4" />
            </>
          ) : (
            <>
              Next
              <ArrowRight className="ml-2 w-4 h-4" />
            </>
          )}
        </Button>
      )}
    </div>
  );
}
